// src/pages/ModificationsPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import AppConst from "../apis/appconst";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const ModificationsPage = () => {
  const getLocalToday = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState({
    module: "",
    action: "",
    search_key: "",
    from: getLocalToday(),
    to: getLocalToday(),
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  const moduleOptions = [
    { value: "", label: "All Modules" },
    { value: "lab_reg", label: "Lab Registration" },
    { value: "lab_item", label: "Lab Item" },
    { value: "lab_payment", label: "Lab Payment" },
    { value: "opd", label: "OPD" },
    { value: "ipd", label: "IPD" },
    { value: "emergency", label: "Emergency" },
    { value: "ot", label: "OT" },
    { value: "med_bill", label: "Pharmacy Bill" },
    { value: "med_payment", label: "Pharmacy Payment" },
    { value: "med_return", label: "Pharmacy Return" },
    { value: "purchase", label: "Purchase" },
  ];

  const moduleLabel = (key) =>
    moduleOptions.find((m) => m.value === key)?.label || key;

  // Modules that support restore (must match what's in LogController::restore_record)
  const RESTORABLE_MODULES = new Set([
    "lab_reg", "lab_item", "lab_payment",
    "opd", "ipd", "emergency", "ot",
    "med_bill", "med_payment", "med_return",
  ]);

  const formatDateTime = (s) => {
    if (!s) return "";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yy} ${hh}:${mi}`;
  };

  const fetchAuditLog = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("module", filters.module || "");
      fd.append("action", filters.action || "");
      fd.append("from", filters.from || "");
      fd.append("to", filters.to || "");
      fd.append("search_key", filters.search_key || "");
      fd.append("page_no", "none");

      const res = await axios.post(
        AppConst.BASE_URL + AppConst.getAuditLogUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data?.status) {
        setRecords(res.data.records || []);
      } else {
        toast.error(res.data?.message || "Failed to load modifications");
        setRecords([]);
      }
    } catch (err) {
      console.error("audit fetch error:", err);
      toast.error("Failed to load audit log");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.module, filters.action, filters.from, filters.to, filters.search_key]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFilters({
      module: "",
      action: "",
      search_key: "",
      from: getLocalToday(),
      to: getLocalToday(),
    });
  };

  const openDetail = async (row) => {
    try {
      const fd = new FormData();
      fd.append("audit_id", row.audit_id);
      const res = await axios.post(
        AppConst.BASE_URL + AppConst.auditDetailUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (res.data?.status) {
        setDetailRow(res.data.record);
        setDetailOpen(true);
      } else {
        setDetailRow(row);
        setDetailOpen(true);
      }
    } catch {
      setDetailRow(row);
      setDetailOpen(true);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailRow(null);
  };

  // ============ RESTORE HANDLER ============
  const handleRestore = async (row) => {
    const confirm = await Swal.fire({
      title: "Restore this record?",
      html: `<div style="text-align:left">
        <strong>Module:</strong> ${moduleLabel(row.audit_module)}<br/>
        <strong>Record:</strong> ${row.audit_record_label || row.audit_record_id}<br/>
        <strong>Deleted on:</strong> ${formatDateTime(row.audit_datetime)}<br/>
        <strong>Deleted by:</strong> ${row.audit_user_name || "—"}
        <hr style="margin:10px 0"/>
        This will re-insert the record (and any related items/payments) back into the database.
      </div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, restore it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#198754",
    });

    if (!confirm.isConfirmed) return;

    setRestoringId(row.audit_id);
    try {
      const fd = new FormData();
      fd.append("audit_id", row.audit_id);
      const res = await axios.post(
        AppConst.BASE_URL + AppConst.restoreRecordUrl,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data?.status) {
        // Build a friendly message from the result
        const r = res.data.result || {};
        let detailLines = [];
        if (r.items_restored !== undefined) detailLines.push(`${r.items_restored} item(s)`);
        if (r.payments_restored !== undefined) detailLines.push(`${r.payments_restored} payment(s)`);
        if (r.bills_restored !== undefined) detailLines.push(`${r.bills_restored} bill(s)`);
        if (r.vals_restored !== undefined) detailLines.push(`${r.vals_restored} vital(s)`);
        if (r.bed_restored) detailLines.push("bed reassigned");
        if (r.amount_re_applied) detailLines.push(`₹${r.amount_re_applied} re-applied`);

        const detailMsg = detailLines.length > 0
          ? ` (${detailLines.join(", ")})`
          : "";

        Swal.fire({
          title: "Restored!",
          text: `Record restored successfully${detailMsg}`,
          icon: "success",
          timer: 3000,
        });

        // Refresh the table so the badge shows up immediately
        fetchAuditLog();
      } else {
        Swal.fire({
          title: "Restore Failed",
          text: res.data?.message || "Could not restore the record",
          icon: "error",
        });
      }
    } catch (err) {
      console.error("restore error:", err);
      Swal.fire({
        title: "Error",
        text: err.response?.data?.message || err.message || "Restore failed",
        icon: "error",
      });
    } finally {
      setRestoringId(null);
    }
  };

  const safeParse = (str) => {
    if (!str) return null;
    if (typeof str !== "string") return str;
    try { return JSON.parse(str); } catch { return null; }
  };

  const buildDiff = (oldData, newData, changedFields) => {
    if (!oldData && !newData) return [];
    const fields = changedFields
      ? changedFields.split(",").filter((f) => f.trim())
      : Object.keys({ ...(oldData || {}), ...(newData || {}) });

    return fields
      .filter((f) => !f.startsWith("_") && !["created_at", "updated_at"].includes(f))
      .map((f) => ({
        field: f,
        oldValue: oldData?.[f] ?? "",
        newValue: newData?.[f] ?? "",
      }));
  };

  const columns = useMemo(
    () => [
      {
        name: "Date / Time",
        selector: (r) => r.audit_datetime,
        sortable: true,
        cell: (r) => <span>{formatDateTime(r.audit_datetime)}</span>,
        width: "160px",
      },
      {
        name: "Module",
        selector: (r) => r.audit_module,
        sortable: true,
        cell: (r) => (
          <span className="badge bg-info">{moduleLabel(r.audit_module)}</span>
        ),
        width: "140px",
      },
      {
        name: "Action",
        selector: (r) => r.audit_action,
        sortable: true,
        cell: (r) => (
          <span
            className={`badge ${
              r.audit_action === "delete"
                ? "bg-danger"
                : r.audit_action === "edit"
                ? "bg-warning text-dark"
                : r.audit_action === "restore"
                ? "bg-primary"
                : "bg-success"
            }`}
          >
            {r.audit_action.toUpperCase()}
          </span>
        ),
        width: "100px",
      },
      {
        name: "Record",
        selector: (r) => r.audit_record_label || r.audit_record_id,
        sortable: true,
        wrap: true,
        cell: (r) => (
          <div style={{ whiteSpace: "normal" }}>
            <div>{r.audit_record_label || "—"}</div>
            <small className="text-muted">ID: {r.audit_record_id}</small>
            {r.audit_restored_at && (
              <div className="mt-1">
                <span className="badge bg-success" style={{ fontSize: "0.7em" }}>
                  ↩ Restored {formatDateTime(r.audit_restored_at)}
                </span>
                {r.audit_restored_by_name && (
                  <small className="text-muted d-block">
                    by {r.audit_restored_by_name}
                  </small>
                )}
              </div>
            )}
          </div>
        ),
        grow: 2,
      },
      {
        name: "User",
        selector: (r) => r.audit_user_name,
        sortable: true,
        cell: (r) => (
          <div>
            <div>{r.audit_user_name || "—"}</div>
            {r.audit_user_role && (
              <small className="text-muted">{r.audit_user_role}</small>
            )}
          </div>
        ),
        width: "150px",
      },
      {
        name: "Changed Fields",
        selector: (r) => r.audit_changed_fields,
        cell: (r) => {
          if (!r.audit_changed_fields) return <span>—</span>;
          const fields = r.audit_changed_fields.split(",").filter(Boolean);
          return (
            <div style={{ whiteSpace: "normal", fontSize: "0.85em" }}>
              {fields.slice(0, 3).join(", ")}
              {fields.length > 3 && (
                <small className="text-muted"> +{fields.length - 3} more</small>
              )}
            </div>
          );
        },
        width: "180px",
      },
      {
        name: "Remarks",
        selector: (r) => r.audit_remarks,
        wrap: true,
        cell: (r) => (
          <div style={{ whiteSpace: "normal", fontSize: "0.85em", maxWidth: "200px" }}>
            {r.audit_remarks || "—"}
          </div>
        ),
        grow: 2,
      },
      {
        name: "Actions",
        cell: (r) => {
          const isDelete = r.audit_action === "delete";
          const isRestorable = isDelete && RESTORABLE_MODULES.has(r.audit_module);
          const alreadyRestored = !!r.audit_restored_at;

          return (
            <div className="d-flex gap-1">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => openDetail(r)}
                title="View details"
              >
                <i className="ri-eye-line"></i>
              </button>
              {isRestorable && !alreadyRestored && (
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={() => handleRestore(r)}
                  disabled={restoringId === r.audit_id}
                  title="Restore this record"
                >
                  {restoringId === r.audit_id ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <>
                      <i className="ri-refresh-line"></i> Restore
                    </>
                  )}
                </button>
              )}
              {alreadyRestored && (
                <span className="badge bg-success-subtle text-success">
                  <i className="ri-check-line"></i> Restored
                </span>
              )}
            </div>
          );
        },
        width: "180px",
        ignoreRowClick: true,
        button: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restoringId]
  );

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f1f5fb",
        color: "#405189",
        fontSize: "14px",
        fontWeight: 600,
      },
    },
    rows: { style: { fontSize: "14px", minHeight: "60px" } },
    cells: { style: { paddingTop: "8px", paddingBottom: "8px" } },
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Modifications Log", pageWidth / 2, 15, { align: "center" });

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(
      `Period: ${filters.from} to ${filters.to} | Total: ${records.length}`,
      pageWidth / 2, 22, { align: "center" }
    );

    const body = records.map((r, i) => [
      i + 1,
      formatDateTime(r.audit_datetime),
      moduleLabel(r.audit_module),
      r.audit_action,
      (r.audit_record_label || r.audit_record_id) + (r.audit_restored_at ? " (RESTORED)" : ""),
      r.audit_user_name || "—",
      r.audit_changed_fields ? r.audit_changed_fields.split(",").slice(0, 3).join(", ") : "—",
      r.audit_remarks || "—",
    ]);

    autoTable(doc, {
      startY: 28,
      head: [["SL", "Date/Time", "Module", "Action", "Record", "User", "Fields", "Remarks"]],
      body,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [64, 81, 137], textColor: 255, fontStyle: "bold" },
    });

    doc.save(`Modifications_${getLocalToday()}.pdf`);
  };

  const handleExportCSV = () => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = AppConst.BASE_URL + AppConst.exportAuditLogUrl;
    form.target = "_blank";
    ["module", "action", "from", "to", "search_key"].forEach((k) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = k;
      input.value = filters[k] || "";
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const stats = useMemo(() => {
    const byAction = { edit: 0, delete: 0, create: 0, restore: 0 };
    let restoredCount = 0;
    records.forEach((r) => {
      byAction[r.audit_action] = (byAction[r.audit_action] || 0) + 1;
      if (r.audit_restored_at) restoredCount++;
    });
    return { byAction, restoredCount };
  }, [records]);

  const detailDiff = useMemo(() => {
    if (!detailRow) return null;
    const oldData = detailRow.audit_old_data_parsed ?? safeParse(detailRow.audit_old_data);
    const newData = detailRow.audit_new_data_parsed ?? safeParse(detailRow.audit_new_data);
    return {
      oldData,
      newData,
      diff: buildDiff(oldData, newData, detailRow.audit_changed_fields),
    };
  }, [detailRow]);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header border-0">
                <h2 className="card-title mb-0" style={{ color: "#405189" }}>
                  Modifications &amp; Audit Log
                </h2>
                <small className="text-muted">
                  All edits and deletions are recorded here. Deleted records can be restored.
                </small>
              </div>

      <div className="card-body border border-dashed border-end-0 border-start-0">
        <div className="row g-3 align-items-end">
          <div className="col-auto">
            <label className="form-label mb-1">From</label>
            <input
              type="date" className="form-control" name="from"
              onChange={handleChange} value={filters.from} style={{ width: "150px" }}
            />
          </div>
          <div className="col-auto">
            <label className="form-label mb-1">To</label>
            <input
              type="date" className="form-control" name="to"
              onChange={handleChange} value={filters.to} style={{ width: "150px" }}
            />
          </div>

          <div className="col-auto">
            <label className="form-label mb-1">Module</label>
            <select
              className="form-select" name="module"
              value={filters.module} onChange={handleChange}
              style={{ minWidth: "180px" }}
            >
              {moduleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="col-auto">
            <label className="form-label mb-1">Action</label>
            <select
              className="form-select" name="action"
              value={filters.action} onChange={handleChange}
              style={{ minWidth: "130px" }}
            >
              <option value="">All Actions</option>
              <option value="edit">Edit</option>
              <option value="delete">Delete</option>
              <option value="restore">Restore</option>
              <option value="create">Create</option>
            </select>
          </div>

          <div className="col" style={{ minWidth: "240px" }}>
            <label className="form-label mb-1">Search</label>
            <input
              className="form-control" name="search_key"
              onChange={handleChange} value={filters.search_key}
              placeholder="Search by record, user, or remarks..."
            />
          </div>

          <div className="col-auto">
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-secondary" onClick={handleReset}>
                <i className="ri-refresh-line me-1"></i> Clear
              </button>
              <button
                type="button" className="btn btn-success"
                onClick={handleExportPDF} disabled={records.length === 0}
              >
                <i className="ri-file-pdf-line me-1"></i> PDF
              </button>
              <button
                type="button" className="btn btn-primary"
                onClick={handleExportCSV} disabled={records.length === 0}
              >
                <i className="ri-file-excel-line me-1"></i> CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      {records.length > 0 && (
        <div className="card-body pt-3 pb-0">
          <div className="d-flex flex-wrap gap-3">
            <span className="badge bg-primary" style={{ fontSize: "0.9rem" }}>
              Total: {records.length}
            </span>
            <span className="badge bg-warning text-dark" style={{ fontSize: "0.9rem" }}>
              Edits: {stats.byAction.edit || 0}
            </span>
            <span className="badge bg-danger" style={{ fontSize: "0.9rem" }}>
              Deletes: {stats.byAction.delete || 0}
            </span>
            {stats.byAction.restore > 0 && (
              <span className="badge bg-info" style={{ fontSize: "0.9rem" }}>
                Restore actions: {stats.byAction.restore}
              </span>
            )}
            {stats.restoredCount > 0 && (
              <span className="badge bg-success" style={{ fontSize: "0.9rem" }}>
                ↩ Records brought back: {stats.restoredCount}
              </span>
            )}
            {stats.byAction.create > 0 && (
              <span className="badge bg-success" style={{ fontSize: "0.9rem" }}>
                Creates: {stats.byAction.create}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="card-body pt-3">
        <DataTable
          columns={columns}
          data={records}
          customStyles={customStyles}
          pagination
          highlightOnHover
          persistTableHead
          progressPending={loading}
          noDataComponent={<div className="text-muted py-3">No modifications found</div>}
        />
      </div>
            </div>
          </div>
        </div>
      </div>

      {detailOpen && detailRow && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-xl" style={{ maxWidth: "1100px" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {detailRow.audit_action.toUpperCase()} - {moduleLabel(detailRow.audit_module)}{" "}
                  <small className="text-muted">
                    by {detailRow.audit_user_name} on {formatDateTime(detailRow.audit_datetime)}
                  </small>
                </h5>
                <button type="button" className="btn-close" onClick={closeDetail} />
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Record:</strong> {detailRow.audit_record_label || "—"}
                  </div>
                  <div className="col-md-6">
                    <strong>Record ID:</strong> {detailRow.audit_record_id}
                  </div>
                </div>

                {detailRow.audit_restored_at && (
                  <div className="alert alert-success py-2">
                    <i className="ri-arrow-go-back-line me-1"></i>
                    <strong>Restored on:</strong> {formatDateTime(detailRow.audit_restored_at)}
                    {detailRow.audit_restored_by_name && <> by <strong>{detailRow.audit_restored_by_name}</strong></>}
                  </div>
                )}

                {detailRow.audit_remarks && (
                  <div className="alert alert-info py-2">
                    <strong>Remarks:</strong> {detailRow.audit_remarks}
                  </div>
                )}

                {detailRow.audit_action === "edit" && detailDiff?.diff?.length > 0 && (
                  <>
                    <h6 className="mt-3">Changed Fields</h6>
                    <table className="table table-bordered table-sm">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "25%" }}>Field</th>
                          <th style={{ width: "37.5%" }}>Old Value</th>
                          <th style={{ width: "37.5%" }}>New Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailDiff.diff.map((d, i) => (
                          <tr key={i}>
                            <td className="fw-bold">{d.field}</td>
                            <td style={{ backgroundColor: "#fff3f3", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {String(d.oldValue || "—")}
                            </td>
                            <td style={{ backgroundColor: "#f0fff0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {String(d.newValue || "—")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {detailRow.audit_action === "delete" && detailDiff?.oldData && (
                  <>
                    <h6 className="mt-3">Deleted Record Snapshot</h6>
                    <table className="table table-bordered table-sm">
                      <tbody>
                        {Object.entries(detailDiff.oldData)
                          .filter(([k]) => !k.startsWith("_"))
                          .map(([k, v]) => (
                            <tr key={k}>
                              <td className="fw-bold" style={{ width: "30%", backgroundColor: "#f8f9fa" }}>
                                {k}
                              </td>
                              <td style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {typeof v === "object" ? JSON.stringify(v, null, 2) : String(v ?? "—")}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>

                    {Object.entries(detailDiff.oldData)
                      .filter(([k, v]) => k.startsWith("_") && Array.isArray(v) && v.length > 0)
                      .map(([k, arr]) => (
                        <details key={k} className="mt-3">
                          <summary className="fw-bold">
                            {k.replace(/^_/, "")} ({arr.length} items)
                          </summary>
                          <pre style={{ backgroundColor: "#f8f9fa", padding: "10px", fontSize: "0.8em", maxHeight: "300px", overflow: "auto" }}>
                            {JSON.stringify(arr, null, 2)}
                          </pre>
                        </details>
                      ))}
                  </>
                )}

                <div className="row mt-3">
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>IP Address:</strong> {detailRow.audit_ip || "—"}
                    </small>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>User Role:</strong> {detailRow.audit_user_role || "—"}
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                {/* Restore button also available from the detail modal */}
                {detailRow.audit_action === "delete"
                  && RESTORABLE_MODULES.has(detailRow.audit_module)
                  && !detailRow.audit_restored_at && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => {
                      closeDetail();
                      handleRestore(detailRow);
                    }}
                  >
                    <i className="ri-refresh-line me-1"></i> Restore this Record
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={closeDetail}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModificationsPage;